import React, { useState, useEffect, useRef } from 'react';
import { messages as messagesAPI } from '../../services/api';
import './ChatWidget.css';

function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchUnreadCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchConversations();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedConversation) {
      fetchConversationMessages();
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [conversationMessages]);

  const fetchUnreadCount = async () => {
    try {
      const response = await messagesAPI.getUnreadCount();
      setUnreadCount(response.data.unreadCount || 0);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await messagesAPI.getConversations();
      setConversations(response.data.conversations || []);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    }
  };

  const fetchConversationMessages = async () => {
    try {
      const response = await messagesAPI.getConversation(selectedConversation.otherUserId);
      setConversationMessages(response.data.messages || []);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleToggle = () => {
    if (isOpen) {
      // Closing
      setIsOpen(false);
      setIsExpanded(false);
      setSelectedConversation(null);
    } else {
      // Opening
      setIsOpen(true);
    }
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setIsExpanded(true);
  };

  const handleBackToList = () => {
    setIsExpanded(false);
    setSelectedConversation(null);
    fetchConversations();
    fetchUnreadCount();
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!messageText.trim() || !selectedConversation) return;

    setSending(true);
    try {
      await messagesAPI.send({
        receiverId: selectedConversation.otherUserId,
        content: messageText.trim(),
      });

      setMessageText('');
      await fetchConversationMessages();
      await fetchConversations();
      await fetchUnreadCount();
    } catch (err) {
      console.error('Failed to send message:', err);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      {/* Chat Button */}
      <button
        className={`chat-widget-button ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
      >
        <span className="chat-icon">💬</span>
        {unreadCount > 0 && (
          <span className="chat-widget-badge">
            <span className="badge-exclamation">!</span>
            <span className="badge-number">{unreadCount}</span>
          </span>
        )}
      </button>

      {/* Chat Modal */}
      {isOpen && (
        <div className={`chat-widget-modal ${isExpanded ? 'expanded' : ''}`}>
          {/* Header */}
          <div className="chat-widget-header">
            {selectedConversation && (
              <button className="back-button-chat" onClick={handleBackToList}>
                ←
              </button>
            )}
            <h3>
              {selectedConversation
                ? selectedConversation.otherUserName
                : 'Messages'}
            </h3>
            <button className="close-button-chat" onClick={handleToggle}>
              ×
            </button>
          </div>

          {/* Content */}
          <div className="chat-widget-content">
            {!selectedConversation ? (
              // Conversation List
              <div className="conversation-list-widget">
                {conversations.length === 0 ? (
                  <div className="empty-conversations">
                    <p>No messages yet</p>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.otherUserId}
                      className="conversation-item-widget"
                      onClick={() => handleSelectConversation(conv)}
                    >
                      <div className="conversation-avatar-widget">
                        {conv.otherUserName.charAt(0).toUpperCase()}
                      </div>
                      <div className="conversation-info-widget">
                        <div className="conversation-name-widget">
                          {conv.otherUserName}
                        </div>
                        <div className="conversation-preview-widget">
                          {conv.lastMessageFrom === 'me' ? 'You: ' : ''}
                          {conv.lastMessage.length > 40
                            ? conv.lastMessage.substring(0, 40) + '...'
                            : conv.lastMessage}
                        </div>
                        <div className="conversation-time-widget">
                          {formatTime(conv.lastMessageTime)}
                        </div>
                      </div>
                      {conv.unreadCount > 0 && (
                        <div className="unread-dot">{conv.unreadCount}</div>
                      )}
                    </div>
                  ))
                )}
              </div>
            ) : (
              // Chat View
              <>
                <div className="chat-messages-widget">
                  {conversationMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`message-bubble-widget ${
                        msg.senderId === selectedConversation.otherUserId
                          ? 'received'
                          : 'sent'
                      }`}
                    >
                      <div className="message-content-widget">
                        {msg.content}
                      </div>
                      <div className="message-time-widget">
                        {formatTime(msg.createdAt)}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                <form className="chat-input-widget" onSubmit={handleSendMessage}>
                  <input
                    type="text"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Type a message..."
                    disabled={sending}
                    maxLength={5000}
                  />
                  <button type="submit" disabled={sending || !messageText.trim()}>
                    ➤
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default ChatWidget;
